// Main subjects shown on the Games landing page
export const STUDENT_GAME_SUBJECTS = [
  {
    key: "maths",
    label: "Maths",
    description: "Boost your mental arithmetic with fun, timed challenges.",
    flashCardDescription: "Learn maths vocabulary and key terms from clues.",
    color: "#1976d2",
    gradient: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
    // Any maths game keys that can be launched for this subject
    games: [
      "math-add",
      "math-sub",
      "math-mul",
      "math-div",
      "math-mixed",
      "clock-mcq",
      "percent-quest",
      "pattern-quest",
      "pie-chart",
      // topic-based maths games
      "number-read-compare",
      "place-value-rounding",
      "prime-factors-multiples",
      "square-cube",
      "negative-sequences",
      "long-mult-div",
      "bodmas",
      "powers-of-10",
      "word-problems",
      "equivalent-fractions",
      "fraction-decimal-percent",
      "fraction-ops",
      "ratio-notation",
      "ratio-proportion-problems",
      "sharing-in-ratio",
      "algebra-notation",
      "algebra-equations",
      "geometry-2d-3d",
      "geometry-angles",
      "geometry-symmetry",
      "geometry-perimeter-area",
      "geometry-volume",
      "metric-conversion",
      "measurement-problems",
      "mean-median-mode",
      "probability-basics",
      "multi-step-reasoning",
      "logic-reasoning",
    ],
  },
  {
    key: "english",
    label: "English",
    description: "Reading, vocabulary, spelling, grammar, and more.",
    flashCardDescription: "Spelling and vocabulary practice from clues.",
    color: "#c2185b",
    gradient: "linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)",
    games: [
      // unscramble game
      "english",
      // MCQ games
      "eng-reading",
      "eng-vocabulary",
      "eng-spelling",
      "eng-grammar",
      "eng-punctuation",
      "eng-creative-writing",
      "eng-cloze",
      "eng-language-techniques",
    ],
  },
  {
    key: "verbal-reasoning",
    label: "Verbal Reasoning",
    description: "Word relationships, letter logic, codes and verbal puzzles.",
    flashCardDescription: "Word and vocabulary practice from clues.",
    color: "#8e24aa",
    gradient: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
    games: [
      "vr-word-relationships",
      "vr-alphabet-logic",
      "vr-word-puzzles",
      "vr-codes-logic",
      "vr-sequence-pattern",
      "vr-comprehension",
    ],
  },
  {
    key: "non-verbal-reasoning",
    label: "Non-Verbal Reasoning",
    description: "Pattern recognition, shape analogies and visual puzzles.",
    flashCardDescription: "Key terms and vocabulary from clues.",
    color: "#ff9800",
    gradient: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
    games: [
      "nvr-pattern-sequence",
      "nvr-shape-analogies",
      "nvr-odd-one-out",
      "nvr-rotation-reflection",
      "nvr-matrix",
      "nvr-spatial",
      "nvr-shape-codes",
      "nvr-hidden-shapes",
    ],
  },
];

/** Maths topics: when a topic is selected, only games in that topic's games array are shown.
 *  Mixed Math Challenge is never shown in topics; it appears only under "All topics". */
/** Years available for flash cards: 3, 4, 5 (Year 3, Year 4, Year 5) */
export const FLASHCARD_YEARS = [3, 4, 5];

export const MATH_TOPICS = [
  {
    key: "number-place-value",
    label: "Number & Place Value",
    years: [3, 4, 5],
    games: ["number-read-compare", "place-value-rounding", "prime-factors-multiples", "square-cube", "negative-sequences"],
  },
  {
    key: "number-lines",
    label: "Number Lines",
    years: [3, 4, 5],
    games: [],
  },
  {
    key: "arithmetic-operations",
    label: "Arithmetic & Operations",
    years: [3, 4, 5],
    games: ["math-add", "math-sub", "math-mul", "math-div", "long-mult-div", "bodmas", "powers-of-10", "word-problems"],
  },
  {
    key: "fractions-decimals-percentages",
    label: "Fractions, Decimals & Percentages",
    years: [3, 4, 5],
    games: ["math-add", "math-sub", "math-mul", "math-div", "percent-quest", "equivalent-fractions", "fraction-decimal-percent", "fraction-ops"],
  },
  {
    key: "ratio-proportion",
    label: "Ratio & Proportion",
    years: [3, 4, 5],
    games: ["ratio-notation", "ratio-proportion-problems", "sharing-in-ratio"],
  },
  {
    key: "algebra-basics",
    label: "Algebra Basics",
    years: [3, 4, 5],
    games: ["algebra-notation", "algebra-equations", "pattern-quest"],
  },
  {
    key: "geometry-shape",
    label: "Geometry & Shape",
    years: [3, 4, 5],
    games: ["geometry-2d-3d", "geometry-angles", "geometry-symmetry", "geometry-perimeter-area", "geometry-volume"],
  },
  {
    key: "measurement",
    label: "Measurement",
    years: [3, 4, 5],
    games: ["clock-mcq", "metric-conversion", "measurement-problems"],
  },
  {
    key: "statistics-data",
    label: "Statistics & Data Handling",
    years: [3, 4, 5],
    games: ["pie-chart", "mean-median-mode", "probability-basics"],
  },
  {
    key: "problem-solving-reasoning",
    label: "Problem-Solving & Reasoning",
    years: [3, 4, 5],
    games: ["word-problems", "mean-median-mode", "probability-basics", "multi-step-reasoning", "logic-reasoning"],
  },
];

/** English topics: when a topic is selected, only games in that topic's games array are shown. */
export const ENGLISH_TOPICS = [
  { key: "reading-comprehension", label: "Reading & Comprehension", years: [3, 4, 5], games: ["eng-reading"] },
  { key: "vocabulary", label: "Vocabulary", years: [3, 4, 5], games: ["english", "eng-vocabulary"] },
  { key: "spelling", label: "Spelling", years: [3, 4, 5], games: ["eng-spelling"] },
  { key: "grammar", label: "Grammar", years: [3, 4, 5], games: ["eng-grammar"] },
  { key: "punctuation", label: "Punctuation", years: [3, 4, 5], games: ["eng-punctuation"] },
  { key: "creative-writing", label: "Creative Writing", years: [3, 4, 5], games: ["eng-creative-writing"] },
  { key: "cloze-sentence-completion", label: "Cloze & Sentence-Completion", years: [3, 4, 5], games: ["eng-cloze"] },
  { key: "language-techniques", label: "Language Techniques", years: [3, 4, 5], games: ["eng-language-techniques"] },
];

/** Verbal Reasoning topics. */
export const VERBAL_REASONING_TOPICS = [
  { key: "word-relationships", label: "Word Relationships & Vocabulary", years: [3, 4, 5], games: ["vr-word-relationships"] },
  { key: "alphabet-letter-logic", label: "Alphabet & Letter Logic", years: [3, 4, 5], games: ["vr-alphabet-logic"] },
  { key: "word-puzzles", label: "Word Puzzles & Formation", years: [3, 4, 5], games: ["vr-word-puzzles"] },
  { key: "codes-logic", label: "Codes & Logic", years: [3, 4, 5], games: ["vr-codes-logic"] },
  { key: "sequence-pattern", label: "Sequence & Pattern Logic", years: [3, 4, 5], games: ["vr-sequence-pattern"] },
  { key: "comprehension-inference", label: "Short Comprehension & Inference", years: [3, 4, 5], games: ["vr-comprehension"] },
];

/** Non-Verbal Reasoning topics. */
export const NON_VERBAL_REASONING_TOPICS = [
  { key: "pattern-sequence", label: "Pattern & Sequence Recognition", years: [3, 4, 5], games: ["nvr-pattern-sequence"] },
  { key: "shape-analogies", label: "Shape Analogies", years: [3, 4, 5], games: ["nvr-shape-analogies"] },
  { key: "odd-one-out", label: "Odd One Out", years: [3, 4, 5], games: ["nvr-odd-one-out"] },
  { key: "rotation-reflection", label: "Figure Rotation & Reflection", years: [3, 4, 5], games: ["nvr-rotation-reflection"] },
  { key: "matrix", label: "Missing Element / Matrix", years: [3, 4, 5], games: ["nvr-matrix"] },
  { key: "spatial-3d", label: "Spatial & 3D Reasoning", years: [3, 4, 5], games: ["nvr-spatial"] },
  { key: "shape-codes", label: "Shape Codes & Manipulation", years: [3, 4, 5], games: ["nvr-shape-codes"] },
  { key: "hidden-shapes", label: "Hidden / Composite Shapes", years: [3, 4, 5], games: ["nvr-hidden-shapes"] },
];

// Helper getters
export const getStudentGameSubjectByKey = (key) =>
  STUDENT_GAME_SUBJECTS.find((subject) => subject.key === key);

export const getMathTopicByKey = (key) =>
  MATH_TOPICS.find((t) => t.key === key);

export const getEnglishTopicByKey = (key) =>
  ENGLISH_TOPICS.find((t) => t.key === key);

export const getVerbalReasoningTopicByKey = (key) =>
  VERBAL_REASONING_TOPICS.find((t) => t.key === key);

export const getNonVerbalReasoningTopicByKey = (key) =>
  NON_VERBAL_REASONING_TOPICS.find((t) => t.key === key);

